import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

const AudioCtx = createContext({ enabled: false, toggle: () => {} });
export const useAmbient = () => useContext(AudioCtx);

export const AudioProvider = ({ children }) => {
  const [enabled, setEnabled] = useState(false);
  const ref = useRef({ ctx: null, drone: null, lastTick: 0 });

  const ensureCtx = () => {
    if (!ref.current.ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      ref.current.ctx = new AC();
    }
    return ref.current.ctx;
  };

  const startDrone = () => {
    const ctx = ensureCtx();
    const gain = ctx.createGain();
    gain.gain.value = 0;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 340;
    const o1 = ctx.createOscillator();
    o1.type = 'sine';
    o1.frequency.value = 56;
    const o2 = ctx.createOscillator();
    o2.type = 'sine';
    o2.frequency.value = 84.5;
    o1.connect(filter);
    o2.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    o1.start();
    o2.start();
    gain.gain.linearRampToValueAtTime(0.035, ctx.currentTime + 2.5);
    ref.current.drone = { o1, o2, gain };
  };

  const stopDrone = () => {
    const d = ref.current.drone;
    const ctx = ref.current.ctx;
    if (d && ctx) {
      d.gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.8);
      const { o1, o2 } = d;
      setTimeout(() => { try { o1.stop(); o2.stop(); } catch (e) { /* noop */ } }, 900);
    }
    ref.current.drone = null;
  };

  const tick = useCallback(() => {
    const state = ref.current;
    if (!state.ctx) return;
    const now = performance.now();
    if (now - state.lastTick < 90) return;
    state.lastTick = now;
    const ctx = state.ctx;
    const g = ctx.createGain();
    g.gain.value = 0;
    const o = ctx.createOscillator();
    o.type = 'triangle';
    o.frequency.value = 1150 + Math.random() * 350;
    o.connect(g);
    g.connect(ctx.destination);
    const t = ctx.currentTime;
    g.gain.linearRampToValueAtTime(0.012, t + 0.004);
    g.gain.linearRampToValueAtTime(0, t + 0.07);
    o.start(t);
    o.stop(t + 0.09);
  }, []);

  const toggle = useCallback(() => { setEnabled((prev) => !prev); }, []);

  useEffect(() => {
    if (!enabled) { stopDrone(); return undefined; }
    const ctx = ensureCtx();
    if (ctx.state === 'suspended') ctx.resume();
    startDrone();
    const handler = (e) => {
      const el = e.target;
      if (el && el.closest && el.closest('button, a, [role="button"], .portal, .r-card, .tech-chip')) tick();
    };
    document.addEventListener('mouseover', handler);
    return () => {
      document.removeEventListener('mouseover', handler);
      stopDrone();
    };
  }, [enabled, tick]);

  return <AudioCtx.Provider value={{ enabled, toggle }}>{children}</AudioCtx.Provider>;
};

export const AudioToggle = ({ className = '' }) => {
  const { enabled, toggle } = useAmbient();
  return (
    <button
      onClick={toggle}
      className={`flex items-center gap-2 font-mono-tech text-[0.58rem] tracking-[0.2em] uppercase text-[#949BAA] hover:text-[#26B0A8] transition-colors ${className}`}
      data-testid="audio-toggle"
      aria-label="Toggle ambient audio"
    >
      {enabled ? <Volume2 className="w-3.5 h-3.5 text-[#1E8F89]" /> : <VolumeX className="w-3.5 h-3.5" />}
      <span className="hidden sm:inline">Audio: {enabled ? 'On' : 'Off'}</span>
    </button>
  );
};

export default AudioProvider;
